import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { requireAuth, requireGuest, requireAdminGuest } from './guards'
import { useAdminStore } from '@/stores/admin'

// Layouts
import PlayerLayout from '@/components/PlayerLayout.vue'
import AdminLayout from '@/components/AdminLayout.vue'

// Error pages
import NotFoundPage from '@/pages/NotFoundPage.vue'

// Player pages — pre-existing pages (eager)
import LobbyPage from '@/pages/player/LobbyPage.vue'
import GameDetailPage from '@/pages/player/GameDetailPage.vue'
import GamePage from '@/pages/player/GamePage.vue'
import WalletPage from '@/pages/player/WalletPage.vue'
import ProvablyFairPage from '@/pages/player/ProvablyFairPage.vue'
import DepositPage from '@/pages/player/DepositPage.vue'
import WithdrawPage from '@/pages/player/WithdrawPage.vue'
import BonusesPage from '@/pages/player/BonusesPage.vue'
import ShopPage from '@/pages/player/ShopPage.vue'

// Auth pages
import LoginPage from '@/pages/auth/LoginPage.vue'
import RegisterPage from '@/pages/auth/RegisterPage.vue'
import VerifyEmailPage from '@/pages/auth/VerifyEmailPage.vue'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage.vue'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage.vue'

// Admin pages
import AdminLoginPage from '@/pages/admin/AdminLoginPage.vue'
import DashboardPage from '@/pages/admin/DashboardPage.vue'
import PlayersPage from '@/pages/admin/PlayersPage.vue'
import PlayerDetailPage from '@/pages/admin/PlayerDetailPage.vue'
import GamesPage from '@/pages/admin/GamesPage.vue'
import FinancePage from '@/pages/admin/FinancePage.vue'
import AdminBonusesPage from '@/pages/admin/BonusesPage.vue'
import PushCampaignsPage from '@/pages/admin/PushCampaignsPage.vue'
import SettingsPage from '@/pages/admin/SettingsPage.vue'
import RbacPage from '@/pages/admin/RbacPage.vue'
import AuditLogPage from '@/pages/admin/AuditLogPage.vue'
import ContentPage from '@/pages/admin/ContentPage.vue'
import AffiliatesPage from '@/pages/admin/AffiliatesPage.vue'
import DesignSystemPage from '@/pages/admin/DesignSystemPage.vue'
import PaymentTransactionsPage from '@/pages/admin/PaymentTransactionsPage.vue'
import PaymentReportsPage from '@/pages/admin/PaymentReportsPage.vue'
import AmlAlertsPage from '@/pages/admin/AmlAlertsPage.vue'
import EddReviewPage from '@/pages/admin/EddReviewPage.vue'
import KycDashboardPage from '@/pages/admin/KycDashboardPage.vue'
import AffordabilityPage from '@/pages/admin/AffordabilityPage.vue'
import JurisdictionPage from '@/pages/admin/JurisdictionPage.vue'
import VpnSettingsPage from '@/pages/admin/VpnSettingsPage.vue'
import JurisdictionReportPage from '@/pages/admin/JurisdictionReportPage.vue'
import UnauthorizedPage from '@/pages/admin/UnauthorizedPage.vue'
import RtpMonitoringPage from '@/pages/admin/RtpMonitoringPage.vue'
import RewardCatalogPage from '@/pages/admin/RewardCatalogPage.vue'
import FulfillmentPage from '@/pages/admin/FulfillmentPage.vue'

const authRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'auth.login',
    component: LoginPage,
    beforeEnter: requireGuest,
    meta: { title: 'Login' },
  },
  {
    path: '/register',
    name: 'auth.register',
    component: RegisterPage,
    beforeEnter: requireGuest,
    meta: { title: 'Register' },
  },
  {
    path: '/verify-email',
    name: 'auth.verify-email',
    component: VerifyEmailPage,
    beforeEnter: requireAuth,
    meta: { title: 'Verify Email' },
  },
  {
    path: '/forgot-password',
    name: 'auth.forgot-password',
    component: ForgotPasswordPage,
    beforeEnter: requireGuest,
    meta: { title: 'Forgot Password' },
  },
  {
    path: '/reset-password',
    name: 'auth.reset-password',
    component: ResetPasswordPage,
    beforeEnter: requireGuest,
    meta: { title: 'Reset Password' },
  },
]

const playerRoutes: RouteRecordRaw[] = [
  {
    path: '/player',
    component: PlayerLayout,
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: '/player/lobby',
      },
      {
        path: 'lobby',
        name: 'player.lobby',
        component: LobbyPage,
        meta: { title: 'Game Lobby' },
      },
      // Category landing pages — must be defined before games/:slug so that
      // static segments take priority over the dynamic parameter.
      {
        path: 'games',
        redirect: '/player/lobby',
      },
      {
        path: 'games/slots',
        name: 'player.games.slots',
        component: LobbyPage,
        props: { defaultCategory: 'slots' },
        meta: { title: 'Slots' },
      },
      {
        path: 'games/table',
        name: 'player.games.table',
        component: LobbyPage,
        props: { defaultCategory: 'table' },
        meta: { title: 'Table Games' },
      },
      {
        path: 'games/originals',
        name: 'player.games.originals',
        component: LobbyPage,
        props: { defaultCategory: 'instant' },
        meta: { title: 'Original Games' },
      },
      {
        // Crash game goes straight to play view
        path: 'games/crash',
        redirect: '/player/games/crash/play',
      },
      {
        path: 'games/:slug',
        name: 'player.game',
        component: GameDetailPage,
        meta: { title: 'Game Details' },
      },
      {
        path: 'games/:slug/play',
        name: 'player.game.play',
        component: GamePage,
        meta: { title: 'Play Game' },
      },
      {
        path: 'wallet',
        name: 'player.wallet',
        component: WalletPage,
        meta: { title: 'Wallet' },
      },
      {
        path: 'deposit',
        name: 'player.deposit',
        component: DepositPage,
        meta: { title: 'Deposit' },
      },
      {
        path: 'withdraw',
        name: 'player.withdraw',
        component: WithdrawPage,
        meta: { title: 'Withdraw' },
      },
      {
        path: 'provably-fair',
        name: 'player.provably-fair',
        component: ProvablyFairPage,
        meta: { title: 'Provably Fair' },
      },
      {
        path: 'profile',
        name: 'player.profile',
        component: () => import('@/pages/player/ProfilePage.vue'),
        meta: { title: 'Profile' },
      },
      {
        path: 'responsible-gaming',
        name: 'player.responsible-gaming',
        component: () => import('@/pages/player/ResponsibleGamingPage.vue'),
        meta: { title: 'Responsible Gaming' },
      },
      {
        path: 'bonuses',
        name: 'player.bonuses',
        component: BonusesPage,
        meta: { title: 'Bonuses' },
      },
      {
        path: 'shop',
        name: 'player.shop',
        component: ShopPage,
        meta: { title: 'Loyalty Shop' },
      },
      {
        path: 'history',
        name: 'player.history',
        component: () => import('@/pages/player/GameHistoryPage.vue'),
        meta: { title: 'Game History' },
      },
      {
        path: 'vip',
        name: 'player.vip',
        component: () => import('@/pages/player/VipPage.vue'),
        meta: { title: 'VIP Program' },
      },
      {
        path: 'leaderboard',
        name: 'player.leaderboard',
        component: () => import('@/pages/player/LeaderboardPage.vue'),
        meta: { title: 'Leaderboard' },
      },
      {
        path: 'affiliates',
        name: 'player.affiliates',
        component: () => import('@/pages/player/AffiliatesPage.vue'),
        meta: { title: 'Affiliate Program' },
      },
      // Live Casino route — reserved for when a live dealer provider is integrated
      {
        path: 'notifications',
        name: 'player.notifications',
        component: () => import('@/pages/player/NotificationsPage.vue'),
        meta: { title: 'Notifications' },
      },
    ],
  },
]

const adminRoutes: RouteRecordRaw[] = [
  {
    path: '/admin/login',
    name: 'admin.login',
    component: AdminLoginPage,
    beforeEnter: requireAdminGuest,
    meta: { title: 'Admin Login' },
  },
  {
    path: '/admin',
    component: AdminLayout,
    meta: { isAdmin: true },
    children: [
      {
        path: '',
        redirect: '/admin/dashboard',
      },
      {
        path: 'dashboard',
        name: 'admin.dashboard',
        component: DashboardPage,
        meta: {
          title: 'Dashboard',
          breadcrumb: ['Admin', 'Dashboard'],
          isAdmin: true,
        },
      },
      {
        path: 'players',
        name: 'admin.players',
        component: PlayersPage,
        meta: {
          title: 'Players',
          breadcrumb: ['Admin', 'Players'],
          isAdmin: true,
          requirePermission: ['view_players'],
        },
      },
      {
        path: 'players/:id',
        name: 'admin.player-detail',
        component: PlayerDetailPage,
        meta: {
          title: 'Player Detail',
          breadcrumb: ['Admin', 'Players', 'Detail'],
          isAdmin: true,
          requirePermission: ['view_players'],
        },
      },
      {
        path: 'games',
        name: 'admin.games',
        component: GamesPage,
        meta: {
          title: 'Games',
          breadcrumb: ['Admin', 'Games'],
          isAdmin: true,
          requirePermission: ['manage_games'],
        },
      },
      {
        path: 'rtp-monitoring',
        name: 'admin.rtp-monitoring',
        component: RtpMonitoringPage,
        meta: {
          title: 'RTP Monitoring',
          breadcrumb: ['Admin', 'Games', 'RTP Monitoring'],
          isAdmin: true,
          requirePermission: ['manage_games'],
        },
      },
      {
        path: 'finance',
        name: 'admin.finance',
        component: FinancePage,
        meta: {
          title: 'Finance',
          breadcrumb: ['Admin', 'Finance'],
          isAdmin: true,
          requirePermission: ['view_finance'],
        },
      },
      {
        path: 'finance/approve',
        name: 'admin.finance.approve',
        component: FinancePage,
        meta: {
          title: 'Approve Withdrawals',
          breadcrumb: ['Admin', 'Finance', 'Approve'],
          isAdmin: true,
          requirePermission: ['approve_withdrawal'],
        },
      },
      {
        path: 'payment-transactions',
        name: 'admin.payment-transactions',
        component: PaymentTransactionsPage,
        meta: {
          title: 'Payment Transactions',
          breadcrumb: ['Admin', 'Payment Transactions'],
          isAdmin: true,
          requirePermission: ['view_finance'],
        },
      },
      {
        path: 'payment-reports',
        name: 'admin.payment-reports',
        component: PaymentReportsPage,
        meta: {
          title: 'Payment Reports',
          breadcrumb: ['Admin', 'Payment Reports'],
          isAdmin: true,
          requirePermission: ['view_finance'],
        },
      },
      {
        path: 'bonuses',
        name: 'admin.bonuses',
        component: AdminBonusesPage,
        meta: {
          title: 'Bonus Campaigns',
          breadcrumb: ['Admin', 'Bonuses'],
          isAdmin: true,
          requirePermission: ['manage_bonuses'],
        },
      },
      {
        path: 'push-campaigns',
        name: 'admin.push-campaigns',
        component: PushCampaignsPage,
        meta: {
          title: 'Push Campaigns',
          breadcrumb: ['Admin', 'Push Campaigns'],
          isAdmin: true,
        },
      },
      {
        path: 'rewards',
        name: 'admin.rewards',
        component: RewardCatalogPage,
        meta: {
          title: 'Reward Catalog',
          breadcrumb: ['Admin', 'Loyalty', 'Rewards'],
          isAdmin: true,
          requirePermission: ['manage_bonuses'],
        },
      },
      {
        path: 'rewards/fulfillment',
        name: 'admin.rewards.fulfillment',
        component: FulfillmentPage,
        meta: {
          title: 'Fulfillment Queue',
          breadcrumb: ['Admin', 'Loyalty', 'Fulfillment'],
          isAdmin: true,
          requirePermission: ['manage_bonuses'],
        },
      },
      {
        path: 'affiliates',
        name: 'admin.affiliates',
        component: AffiliatesPage,
        meta: {
          title: 'Affiliates',
          breadcrumb: ['Admin', 'Affiliates'],
          isAdmin: true,
          requirePermission: ['manage_affiliates'],
        },
      },
      {
        path: 'settings',
        name: 'admin.settings',
        component: SettingsPage,
        meta: {
          title: 'Settings',
          breadcrumb: ['Admin', 'Settings'],
          isAdmin: true,
          requireRole: ['super_admin', 'admin'],
        },
      },
      {
        path: 'rbac',
        name: 'admin.rbac',
        component: RbacPage,
        meta: {
          title: 'RBAC',
          breadcrumb: ['Admin', 'RBAC'],
          isAdmin: true,
          requireRole: ['super_admin'],
        },
      },
      {
        path: 'aml-alerts',
        name: 'admin.aml-alerts',
        component: AmlAlertsPage,
        meta: {
          title: 'AML Alerts',
          breadcrumb: ['Admin', 'AML Alerts'],
          isAdmin: true,
          requirePermission: ['view_finance'],
        },
      },
      {
        path: 'edd-review',
        name: 'admin.edd-review',
        component: EddReviewPage,
        meta: {
          title: 'EDD Review',
          breadcrumb: ['Admin', 'EDD Review'],
          isAdmin: true,
          requirePermission: ['view_players'],
        },
      },
      {
        path: 'kyc-dashboard',
        name: 'admin.kyc-dashboard',
        component: KycDashboardPage,
        meta: {
          title: 'KYC Dashboard',
          breadcrumb: ['Admin', 'KYC Dashboard'],
          isAdmin: true,
          requirePermission: ['view_players'],
        },
      },
      {
        path: 'affordability',
        name: 'admin.affordability',
        component: AffordabilityPage,
        meta: {
          title: 'Affordability Assessments',
          breadcrumb: ['Admin', 'Affordability'],
          isAdmin: true,
          requirePermission: ['view_finance'],
        },
      },
      {
        path: 'jurisdiction',
        name: 'admin.jurisdiction',
        component: JurisdictionPage,
        meta: {
          title: 'Geo-Fencing',
          breadcrumb: ['Admin', 'Geo-Fencing'],
          isAdmin: true,
          requireRole: ['super_admin', 'admin'],
        },
      },
      {
        path: 'vpn-settings',
        name: 'admin.vpn-settings',
        component: VpnSettingsPage,
        meta: {
          title: 'VPN Detection',
          breadcrumb: ['Admin', 'VPN Detection'],
          isAdmin: true,
          requireRole: ['super_admin', 'admin'],
        },
      },
      {
        path: 'jurisdiction-report',
        name: 'admin.jurisdiction-report',
        component: JurisdictionReportPage,
        meta: {
          title: 'Jurisdiction Report',
          breadcrumb: ['Admin', 'Jurisdiction Report'],
          isAdmin: true,
          requirePermission: ['view_players'],
        },
      },
      {
        path: 'audit-log',
        name: 'admin.audit-log',
        component: AuditLogPage,
        meta: {
          title: 'Audit Log',
          breadcrumb: ['Admin', 'Audit Log'],
          isAdmin: true,
          requirePermission: ['view_audit_log'],
        },
      },
      {
        path: 'content',
        name: 'admin.content',
        component: ContentPage,
        meta: {
          title: 'Content Management',
          breadcrumb: ['Admin', 'Content'],
          isAdmin: true,
          requirePermission: ['manage_content'],
        },
      },
      {
        path: 'design-system',
        name: 'admin.design-system',
        component: DesignSystemPage,
        meta: {
          title: 'Design System',
          breadcrumb: ['Admin', 'Design System'],
          isAdmin: true,
          requireRole: ['super_admin', 'admin'],
        },
      },
      {
        path: 'unauthorized',
        name: 'admin.unauthorized',
        component: UnauthorizedPage,
        meta: { title: 'Unauthorized', isAdmin: true },
      },
    ],
  },
]

const publicRoutes: RouteRecordRaw[] = [
  {
    path: '/provably-fair',
    name: 'provably-fair.verify',
    component: () => import('@/pages/public/ProvablyFairVerifyPage.vue'),
    meta: { title: 'Verify Provably Fair' },
  },
  {
    // CMS page without locale prefix — uses current i18n locale
    path: '/pages/:slug',
    name: 'cms.page',
    component: () => import('@/pages/public/CmsPage.vue'),
    meta: { title: 'Page' },
  },
  {
    // CMS page with locale prefix — e.g. /de/pages/terms
    path: '/:locale(en|de|pt|es|fr|tr)/pages/:slug',
    name: 'cms.page.localized',
    component: () => import('@/pages/public/CmsPage.vue'),
    meta: { title: 'Page' },
  },
]

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/player/lobby',
  },
  ...authRoutes,
  ...publicRoutes,
  ...playerRoutes,
  ...adminRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundPage,
    meta: { title: '404 — Page Not Found' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Global guard: enforce RBAC on admin routes
router.beforeEach(async (to) => {
  // Only check admin routes (identified by isAdmin meta)
  const isAdminRoute = to.matched.some((record) => record.meta.isAdmin)
  if (!isAdminRoute) return true

  // Allow login and unauthorized pages without auth check
  if (to.name === 'admin.login' || to.name === 'admin.unauthorized') return true

  const adminStore = useAdminStore()
  await adminStore.waitForInit()

  // Check admin authentication — redirect to login
  if (!adminStore.isAuthenticated) {
    return { name: 'admin.login' }
  }

  // Check required roles (user must have one of the listed roles)
  const requiredRoles = to.meta.requireRole ?? []
  if (requiredRoles.length > 0 && !adminStore.hasRole(requiredRoles)) {
    return { name: 'admin.unauthorized' }
  }

  // Check required permissions (user must have at least one of the listed permissions)
  const requiredPermissions = to.meta.requirePermission ?? []
  if (requiredPermissions.length > 0 && !adminStore.hasPermission(requiredPermissions)) {
    return { name: 'admin.unauthorized' }
  }

  return true
})

export default router
